/**
 * Sign In Page
 * Login page with email/password and phone/OTP options
 * 
 * @author Varun Pandey
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PhoneInput } from '../../components/ui/PhoneInput';
import { OTPInput } from '../../components/ui/OTPInput';
import { Loader } from '../../components/ui/Loader';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { AuthService, getAuthErrorMessage, handleSsoResult } from '../../services/authService';
import { useArtistDetailsStore } from '../../stores/artistDetailsStore';
import { useAuthStore } from '../../stores/authStore';
import {
  getNativeAccessTokenFromCookies,
  getNativeRefreshTokenFromCookies,
} from '@/utils/nativeAuthCookies';
import { setAuthRoleCookies } from '../../utils/cookies';
import { GoogleLogin } from '@react-oauth/google';
import { STORAGE_KEYS } from '../../storage/storageKeys';
import { storageManager } from '../../storage/storageManager';
import { cn } from '../../utils/cn';
import { canUseTruecaller, generateTruecallerRequestNonce, buildTruecallerDeepLink } from '../../utils/truecaller';
import { INDIAN_PHONE_ERROR_MESSAGE, isValidIndianPhoneNumber } from '../../utils/phone';
import TrueCallerIcon from '../../assets/images/TrueCallerIcon.png';
import { InstagramAuthCore } from '@/instagram/InstagramAuthCore';
import { Capacitor } from '@capacitor/core';
import { GoogleAuthCore } from '@/auth/GoogleAuthCore';
import { AppleAuthCore } from '@/auth/AppleAuthCore';

type LoginMethod = 'email' | 'phone';
type PhoneLoginType = 'otp' | 'password';

export const SignInPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fetchArtistDetails = useArtistDetailsStore((state) => state.fetchArtistDetails);
  const logout = useAuthStore((state) => state.logout);
  const setTokens = useAuthStore((state) => state.setTokens);
  const isNativeApp = Capacitor.isNativePlatform();
  const isNativeIOS = isNativeApp && Capacitor.getPlatform() === 'ios';

  // Show toast when redirected (session expired) - from ProtectedRoute (state) or details API 401 (sessionStorage)
  const hasShownRedirectToast = useRef(false);
  useEffect(() => {
    if (hasShownRedirectToast.current) return;
    const logoutSuccessMessage = sessionStorage.getItem(STORAGE_KEYS.LOGOUT_SUCCESS_MESSAGE);
    if (logoutSuccessMessage) {
      hasShownRedirectToast.current = true;
      toast.success(logoutSuccessMessage);
      sessionStorage.removeItem(STORAGE_KEYS.LOGOUT_SUCCESS_MESSAGE);
      sessionStorage.removeItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST);
      return;
    }

    const messageFromState = (location.state as { message?: string })?.message;
    const messageFromStorage = sessionStorage.getItem(STORAGE_KEYS.LOGIN_REDIRECT_MESSAGE);
    const message = messageFromState ?? messageFromStorage;
    if (message) {
      hasShownRedirectToast.current = true;
      toast.error(message);
      // Hard reset local/session state when we know the session has expired
      // so we don't keep trying to auto-navigate back into protected routes.
      void logout();
      if (messageFromState) navigate(location.pathname, { replace: true, state: {} });
      if (messageFromStorage) sessionStorage.removeItem(STORAGE_KEYS.LOGIN_REDIRECT_MESSAGE);
      sessionStorage.removeItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST);
    }

    // Cleanup any stale suppression flag once we are already on login.
    sessionStorage.removeItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST);
  }, [location.state, location.pathname, navigate, logout]);

  // Tab state
  const [activeTab, setActiveTab] = useState<LoginMethod>('email');

  // Email form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Phone form state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneLoginType, setPhoneLoginType] = useState<PhoneLoginType>('otp');
  const [otp, setOtp] = useState('');
  const [phonePassword, setPhonePassword] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  // Common state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isLoggingInWithPassword, setIsLoggingInWithPassword] = useState(false);
  const [isSsoLoading, setIsSsoLoading] = useState(false);


  // Refs for auto-focus
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const phonePasswordInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus phone input when phone tab is selected
  useEffect(() => {
    if (activeTab === 'phone' && phoneInputRef.current && !otpSent) {
      setTimeout(() => {
        phoneInputRef.current?.focus();
      }, 100);
    }
  }, [activeTab, otpSent]);

  // Auto-focus password input when it appears
  useEffect(() => {
    if (activeTab === 'phone' && phoneLoginType === 'password' && phonePasswordInputRef.current) {
      setTimeout(() => {
        phonePasswordInputRef.current?.focus();
      }, 100);
    }
  }, [activeTab, phoneLoginType]);

  // Auto-focus OTP input when OTP is sent
  useEffect(() => {
    if (otpSent && activeTab === 'phone') {
      // OTPInput has autoFocus prop, so it will handle this
    }
  }, [otpSent, activeTab]);

  const validateEmail = () => {
    const newErrors: Record<string, string> = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePhone = (otpValue?: string) => {
    const newErrors: Record<string, string> = {};
    if (!phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!isValidIndianPhoneNumber(phoneNumber)) {
      newErrors.phoneNumber = INDIAN_PHONE_ERROR_MESSAGE;
    }

    if (phoneLoginType === 'password' && !phonePassword) {
      newErrors.phonePassword = 'Password is required';
    }

    if (otpSent && phoneLoginType === 'otp') {
      // Use provided OTP value or fall back to state
      const currentOtp = (otpValue !== undefined ? otpValue : otp).trim();
      if (!currentOtp || currentOtp.length === 0) {
        newErrors.otp = 'Please enter the 4-digit OTP';
      } else if (currentOtp.length !== 4 || !/^\d{4}$/.test(currentOtp)) {
        newErrors.otp = 'Please enter a valid 4-digit OTP';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOTP = async () => {
    if (!validatePhone()) {
      if (phoneNumber && !isValidIndianPhoneNumber(phoneNumber)) {
        toast.error(INDIAN_PHONE_ERROR_MESSAGE);
      }
      return;
    }

    setIsSendingOTP(true);
    setErrors({});
    try {
      await AuthService.requestOtp(phoneNumber);
      toast.success('OTP sent successfully');
      setOtpSent(true);
    } catch (error) {
      const message = getAuthErrorMessage(error, 'Failed to send OTP. Please try again.');
      toast.error(message);
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handlePostLogin = async (result: Awaited<ReturnType<typeof AuthService.login>>) => {
    if (!useAuthStore.getState().accessToken) {
      const { token } = await getNativeAccessTokenFromCookies();
      if (token) {
        const refresh = (await getNativeRefreshTokenFromCookies()) ?? '';
        await setTokens(token, refresh);
        console.log('[AUTH] Token saved after login:', token.slice(0, 20));
      } else {
        console.warn('[AUTH] No token in login response — cookie-only auth');
      }
    }

    toast.success('Logged in successfully.');
    const loginData = result.loginData;
    const role = (loginData?.role ?? result.user?.role ?? 'individual').trim();
    const isAgency =
      role === 'agency' ||
      role === 'agency_owner' ||
      role === 'sub_user';

    if (loginData?.onboarding_completed === false) {
      // Persist signup progress so user can resume onboarding (steps 4–6) if they drop off
      try {
        await storageManager.set(STORAGE_KEYS.SIGNUP_PROGRESS, {
          step: 4,
          source: 'normal',
          fullName: result.user?.name,
          email: result.user?.email,
        });
      } catch (err) {
        console.error('Failed to persist signup progress from login:', err);
      }

      navigate('/signup', { state: { startStep: 4, source: 'normal', fullName: result.user?.name, email: result.user?.email } });
      return;
    }
    if (loginData?.onboarding_completed === true && isAgency) {
      setAuthRoleCookies('agency');
      navigate('/agency-home');
      return;
    }
    if (loginData?.onboarding_completed === true && !isAgency) {
      setAuthRoleCookies(role);
      await fetchArtistDetails();
      sessionStorage.setItem(STORAGE_KEYS.LOGIN_TO_USER_HOME, '1');
      navigate('/user-home');
      return;
    }
    // Fallback when loginData is missing (e.g. legacy API)
    if (isAgency) {
      setAuthRoleCookies('agency');
      navigate('/agency-home');
    } else {
      setAuthRoleCookies(role);
      await fetchArtistDetails();
      sessionStorage.setItem(STORAGE_KEYS.LOGIN_TO_USER_HOME, '1');
      navigate('/user-home');
    }
  };

  const handlePhoneLogin = async (otpValue?: string) => {
    const currentOtp = otpValue !== undefined ? otpValue : otp;

    if (!validatePhone(currentOtp)) {
      if (phoneNumber && !isValidIndianPhoneNumber(phoneNumber)) {
        toast.error(INDIAN_PHONE_ERROR_MESSAGE);
      }
      return;
    }

    if (phoneLoginType === 'otp') {
      setIsLoading(true);
    } else {
      setIsLoggingInWithPassword(true);
    }

    setErrors({});
    try {
      if (phoneLoginType === 'otp') {
        const result = await AuthService.verifyOtp(phoneNumber, currentOtp);
        await handlePostLogin(result);
      } else {
        const result = await AuthService.login({
          phone: phoneNumber,
          password: phonePassword,
        });
        await handlePostLogin(result);
      }
    } catch (error) {
      const fallback =
        phoneLoginType === 'otp' ? 'Invalid or expired OTP. Please try again.' : 'Invalid phone number or password.';
      toast.error(getAuthErrorMessage(error, fallback));
    } finally {
      setIsLoading(false);
      setIsLoggingInWithPassword(false);
    }
  };

  const handleEmailSignIn = async () => {
    if (!validateEmail()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      const result = await AuthService.login({ email, password });
      await handlePostLogin(result);
    } catch (error) {
      toast.error(getAuthErrorMessage(error, 'Invalid email or password. Please check your credentials.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
    const credential = credentialResponse.credential;
    if (!credential) {
      toast.error('Google sign-in did not return a credential. Please try again.');
      return;
    }
    setIsSsoLoading(true);
    try {
      const result = await AuthService.signupSso('google', credential);
      await handleSsoResult(result, navigate, fetchArtistDetails);
    } catch (err) {
      toast.error(getAuthErrorMessage(err, 'Google sign-in failed. Please try again.'));
      setIsSsoLoading(false);
    }
  };

  const startTruecallerPolling = async (requestId: string) => {
    const POLL_INTERVAL_MS = 3000;
    const POLL_MAX_ATTEMPTS = 5;

    for (let attempt = 1; attempt <= POLL_MAX_ATTEMPTS; attempt++) {
      try {
        const result = await AuthService.signupSso('truecaller', requestId);
        await handleSsoResult(result, navigate, fetchArtistDetails);
        return;
      } catch (error: any) {
        if (attempt === POLL_MAX_ATTEMPTS) {
          const fallback = 'Truecaller verification did not complete. Please try again or use phone/email.';
          toast.error(getAuthErrorMessage(error, fallback));
          setIsSsoLoading(false);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
    }
  };

  const handleTruecallerSignIn = () => {
    if (!canUseTruecaller()) {
      toast.error('Truecaller sign-in is only available on Android devices.');
      return;
    }

    const requestId = generateTruecallerRequestNonce();
    const deepLink = buildTruecallerDeepLink(requestId);
    if (!deepLink) {
      toast.error('Truecaller is not configured. Please try again later.');
      return;
    }

    setIsSsoLoading(true);

    if (typeof window !== 'undefined') {
      window.location.href = deepLink;
    }

    // Focus checks are unreliable on mobile webviews; always poll backend
    // after deep-link trigger and let backend response determine completion.
    setTimeout(() => {
      void startTruecallerPolling(requestId);
    }, 600);
  };

  const handleOTPComplete = (value: string) => {
    const trimmedValue = value.trim();
    setOtp(trimmedValue);
    // Clear any existing OTP errors
    if (errors.otp) {
      setErrors((prev) => ({ ...prev, otp: '' }));
    }
    // Auto-submit when OTP is complete (exactly 4 digits)
    // Pass the OTP value directly to avoid state timing issues
    if (trimmedValue.length === 4 && /^\d{4}$/.test(trimmedValue)) {
      handlePhoneLogin(trimmedValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, handler: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handler();
    }
  };

  return (
    <AuthLayout showImage={false} backgroundVariant="spotlight">
      <div className="space-y-4 px-4 pt-8 pb-2 lg:px-0 lg:py-8">
        {/* <Logo size="md" className="mb-4" /> */}
        <h1 className="text-4xl font-bold bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent">Welcome back!</h1>
        <p className="text-sm text-[#8E8E93]">Sign in to continue to your account.</p>

        {/* Tabs */}
        <div className="relative flex border-b border-[#2C2C2E] bg-transparent">
          {/* Sliding underline indicator */}
          <div
            className="absolute bottom-0 h-0.5 bg-[#F33363] rounded-t-full transition-all duration-300 ease-in-out"
            style={{
              left: activeTab === 'phone' ? '0%' : '50%',
              width: '50%',
              transform: 'translateZ(0)',
              WebkitTransform: 'translateZ(0)',
            }}
          />

          <button
            type="button"
            onClick={() => {
              setActiveTab('phone');
              setErrors({});
              setOtpSent(false);
              setOtp('');
            }}
            className={cn(
              'flex flex-1 items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors duration-300 relative z-10 bg-transparent',
              activeTab === 'phone'
                ? 'text-[#F33363]'
                : 'text-[#8E8E93] hover:text-white'
            )}
          >
            <svg
              className="h-4 w-4 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            Phone
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('email');
              setErrors({});
            }}
            className={cn(
              'flex flex-1 items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors duration-300 relative z-10 bg-transparent',
              activeTab === 'email'
                ? 'text-[#F33363]'
                : 'text-[#8E8E93] hover:text-white'
            )}
          >
            <svg
              className="h-4 w-4 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Email
          </button>
        </div>

        {/* Tab Content Wrapper */}
        <div className="relative">
          {/* Phone Tab Content */}
          <div
            style={{
              position: activeTab === 'phone' ? 'relative' : 'absolute',
              width: '100%',
              opacity: activeTab === 'phone' ? 1 : 0,
              transform: activeTab === 'phone' ? 'translateY(0) translateZ(0)' : 'translateY(-10px) translateZ(0)',
              transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              WebkitTransform: activeTab === 'phone' ? 'translateY(0) translateZ(0)' : 'translateY(-10px) translateZ(0)',
              WebkitBackfaceVisibility: 'hidden',
              WebkitPerspective: '1000px',
              backfaceVisibility: 'hidden',
              willChange: 'opacity, transform',
              pointerEvents: activeTab === 'phone' ? 'auto' : 'none',
              visibility: activeTab === 'phone' ? 'visible' : 'hidden',
            }}
          >
            <form
              className="space-y-4"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!otpSent) {
                    if (phoneLoginType === 'otp') {
                      handleSendOTP();
                    } else {
                      handlePhoneLogin();
                    }
                  } else {
                    handlePhoneLogin();
                  }
                }
              }}
              onSubmit={(e) => {
                e.preventDefault();
                if (!otpSent) {
                  if (phoneLoginType === 'otp') {
                    handleSendOTP();
                  } else {
                    handlePhoneLogin();
                  }
                } else {
                  handlePhoneLogin();
                }
              }}
            >
              <PhoneInput
                ref={phoneInputRef}
                label="Phone number"
                value={phoneNumber}
                onChange={(value) => {
                  setPhoneNumber(value);
                  if (errors.phoneNumber) {
                    setErrors((prev) => ({ ...prev, phoneNumber: '' }));
                  }
                }}
                error={errors.phoneNumber}
                disabled={otpSent}
              />

              {/* Login using toggle */}
              {!otpSent && (
                <div className="flex items-center justify-between rounded-[0.75rem] px-4">
                  <span className="text-sm text-[#8E8E93]">Login using</span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setPhoneLoginType('otp')}
                      className={cn(
                        'text-sm font-medium transition-colors',
                        phoneLoginType === 'otp' ? 'text-[#F33363]' : 'text-[#8E8E93]'
                      )}
                    >
                      OTP
                    </button>
                    <div
                      className={cn(
                        'relative h-6 w-11 rounded-full transition-colors cursor-pointer',
                        phoneLoginType === 'password' ? 'bg-[#F33363]' : 'bg-[#3A3A3C]'
                      )}
                      onClick={() => setPhoneLoginType(phoneLoginType === 'otp' ? 'password' : 'otp')}
                    >
                      <div
                        className={cn(
                          'absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform',
                          phoneLoginType === 'password' ? 'translate-x-5' : 'translate-x-0.5'
                        )}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setPhoneLoginType('password')}
                      className={cn(
                        'text-sm font-medium transition-colors',
                        phoneLoginType === 'password' ? 'text-[#F33363]' : 'text-[#8E8E93]'
                      )}
                    >
                      Password
                    </button>
                  </div>
                </div>
              )}

              {/* Password input - slides in when password is selected */}
              {!otpSent && (
                <div
                  style={{
                    maxHeight: phoneLoginType === 'password' ? '200px' : '0',
                    opacity: phoneLoginType === 'password' ? 1 : 0,
                    transform: phoneLoginType === 'password' ? 'translateY(0) translateZ(0)' : 'translateY(-10px) translateZ(0)',
                    overflow: phoneLoginType === 'password' ? 'visible' : 'hidden',
                    paddingTop: phoneLoginType === 'password' ? '0.5rem' : '0',
                    paddingBottom: phoneLoginType === 'password' ? '0.5rem' : '0',
                    marginTop: phoneLoginType === 'password' ? '0.5rem' : '0',
                    transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding-top 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding-bottom 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin-top 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    WebkitTransform: phoneLoginType === 'password' ? 'translateY(0) translateZ(0)' : 'translateY(-10px) translateZ(0)',
                    WebkitBackfaceVisibility: 'hidden',
                    WebkitPerspective: '1000px',
                    backfaceVisibility: 'hidden',
                    willChange: 'max-height, opacity, transform',
                    pointerEvents: phoneLoginType === 'password' ? 'auto' : 'none',
                  }}
                >
                  <Input
                    ref={phonePasswordInputRef}
                    label="Password"
                    type="password"
                    placeholder="Enter your password"
                    value={phonePassword}
                    onChange={(e) => {
                      setPhonePassword(e.target.value);
                      if (errors.phonePassword) {
                        setErrors((prev) => ({ ...prev, phonePassword: '' }));
                      }
                    }}
                    error={errors.phonePassword}
                    showPasswordToggle
                  />
                  {phoneLoginType === 'password' && (
                    <div className="mt-2 flex justify-end">
                      <Link
                        to="/forgot-password"
                        className="text-sm font-medium text-[#F33363] hover:text-[#D92D58] transition-colors"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* OTP input - slides down when OTP is sent */}
              {phoneLoginType === 'otp' && (
                <div
                  style={{
                    maxHeight: otpSent ? '300px' : '0',
                    opacity: otpSent ? 1 : 0,
                    transform: otpSent ? 'translateY(0) translateZ(0)' : 'translateY(-10px) translateZ(0)',
                    overflow: otpSent ? 'visible' : 'hidden',
                    paddingTop: otpSent ? '0.5rem' : '0',
                    paddingBottom: otpSent ? '0.5rem' : '0',
                    transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding-top 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding-bottom 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    WebkitTransform: otpSent ? 'translateY(0) translateZ(0)' : 'translateY(-10px) translateZ(0)',
                    WebkitBackfaceVisibility: 'hidden',
                    WebkitPerspective: '1000px',
                    backfaceVisibility: 'hidden',
                    willChange: 'max-height, opacity, transform',
                    pointerEvents: otpSent ? 'auto' : 'none',
                  }}
                >
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-white">Enter OTP</label>
                    <OTPInput
                      length={4}
                      value={otp}
                      onChange={(value) => {
                        const trimmedValue = value.trim();
                        setOtp(trimmedValue);
                        // Clear error when user starts typing
                        if (errors.otp) {
                          setErrors((prev) => ({ ...prev, otp: '' }));
                        }
                        // If OTP is complete, trigger validation
                        if (trimmedValue.length === 4 && /^\d{4}$/.test(trimmedValue)) {
                          // Clear any errors since OTP is valid
                          setErrors((prev) => ({ ...prev, otp: '' }));
                        }
                      }}
                      onComplete={handleOTPComplete}
                      autoFocus={otpSent}
                    />
                    {errors.otp && (
                      <p className="text-xs text-destructive">{errors.otp}</p>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setOtpSent(false);
                        setOtp('');
                      }}
                      className="text-sm font-medium text-[#F33363] hover:text-[#D92D58] transition-colors"
                    >
                      Change phone number
                    </button>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                variant="active"
                className="w-full"
                onClick={(e) => {
                  e.preventDefault();
                  if (!otpSent) {
                    if (phoneLoginType === 'otp') {
                      handleSendOTP();
                    } else {
                      handlePhoneLogin();
                    }
                  } else {
                    handlePhoneLogin();
                  }
                }}
                isLoading={otpSent ? isLoading : phoneLoginType === 'otp' ? isSendingOTP : isLoggingInWithPassword}
              >
                {otpSent ? 'Login' : phoneLoginType === 'otp' ? 'Send OTP' : 'Login'}
              </Button>
            </form>
          </div>

          {/* Email Tab Content */}
          <div
            style={{
              position: activeTab === 'email' ? 'relative' : 'absolute',
              width: '100%',
              opacity: activeTab === 'email' ? 1 : 0,
              transform: activeTab === 'email' ? 'translateY(0) translateZ(0)' : 'translateY(-10px) translateZ(0)',
              transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              WebkitTransform: activeTab === 'email' ? 'translateY(0) translateZ(0)' : 'translateY(-10px) translateZ(0)',
              WebkitBackfaceVisibility: 'hidden',
              WebkitPerspective: '1000px',
              backfaceVisibility: 'hidden',
              willChange: 'opacity, transform',
              pointerEvents: activeTab === 'email' ? 'auto' : 'none',
              visibility: activeTab === 'email' ? 'visible' : 'hidden',
            }}
          >
            <form
              className="space-y-4"
              onKeyDown={(e) => handleKeyDown(e, handleEmailSignIn)}
              onSubmit={(e) => {
                e.preventDefault();
                handleEmailSignIn();
              }}
            >
              <Input
                label="Email address"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) {
                    setErrors((prev) => ({ ...prev, email: '' }));
                  }
                }}
                error={errors.email}
              />
              <div>
                <Input
                  ref={passwordInputRef}
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) {
                      setErrors((prev) => ({ ...prev, password: '' }));
                    }
                  }}
                  error={errors.password}
                  showPasswordToggle
                />
                <div className="mt-2 flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-[#F33363] hover:text-[#D92D58] transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                variant="active"
                className="w-full"
                onClick={handleEmailSignIn}
                isLoading={isLoading}
              >
                Sign In
              </Button>
            </form>
          </div>
        </div>

        <div className="text-center -mt-2">
          <p className="text-sm text-[#8E8E93]">
            New here?{' '}
            <Link
              to="/signup"
              className="font-semibold text-[#F33363] hover:text-[#D92D58] transition-colors"
            >
              Create an account
            </Link>
          </p>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[#2C2C2E]" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-[#0B0B0D] px-2 text-[#8E8E93]">or sign in using</span>
          </div>
        </div>

        <div className="flex justify-center gap-4 items-center">
          {canUseTruecaller() && (
            <button
              type="button"
              onClick={handleTruecallerSignIn}
              className="flex h-12 w-12 items-center justify-center rounded-lg transition-colors"
              aria-label="Sign in with Truecaller"
              disabled={isSsoLoading}
            >
              <img src={TrueCallerIcon} alt="Truecaller" className="h-10 w-10" />
            </button>
          )}
          {isNativeApp ? (
            <button
              type="button"
              className={cn(
                "flex h-12 w-12 items-center justify-center rounded-full bg-white hover:bg-gray-100 transition-colors",
                isSsoLoading && "opacity-60 pointer-events-none",
              )}
              aria-label="Sign in with Google"
              onClick={async () => {
                if (isSsoLoading) return;
                setIsSsoLoading(true);
                try {
                  const idToken = await GoogleAuthCore.signIn();
                  const result = await AuthService.signupSso('google', idToken);
                  await handleSsoResult(result, navigate, fetchArtistDetails);
                } catch (err) {
                  console.error("Google login OAuth error:", err);
                  toast.error("Unable to start Google sign-in. Please try again.");
                  setIsSsoLoading(false);
                }
              }}
              disabled={isSsoLoading}
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            </button>
          ) : (
            <div
              className={cn('flex items-center justify-center', isSsoLoading && 'opacity-60 pointer-events-none')}
              onMouseDown={() => {
                if (!isSsoLoading) setIsSsoLoading(true);
              }}
              onTouchStart={() => {
                if (!isSsoLoading) setIsSsoLoading(true);
              }}
            >
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => {
                  toast.error('Google sign-in was cancelled or failed.');
                  setIsSsoLoading(false);
                }}
                theme="filled_black"
                size="large"
                type="icon"
                shape="circle"
                useOneTap={false}
              />
            </div>
          )}
          {isNativeIOS && (
            <button
              type="button"
              className={cn(
                "flex h-12 w-12 items-center justify-center rounded-full bg-black border border-white/20 hover:bg-neutral-900 transition-colors",
                isSsoLoading && "opacity-60 pointer-events-none",
              )}
              aria-label="Sign in with Apple"
              onClick={async () => {
                if (isSsoLoading) return;
                setIsSsoLoading(true);
                try {
                  const apple = await AppleAuthCore.signIn();
                  const result = await AuthService.signupSso('apple', apple.idToken, {
                    givenName: apple.givenName,
                    familyName: apple.familyName,
                    email: apple.email,
                    fullName: apple.fullName,
                  });
                  await handleSsoResult(result, navigate, fetchArtistDetails, {
                    email: apple.email,
                    fullName: apple.fullName,
                    givenName: apple.givenName,
                    familyName: apple.familyName,
                  });
                } catch (err) {
                  console.error("Apple login OAuth error:", err);
                  toast.error(getAuthErrorMessage(err, "Unable to start Apple sign-in. Please try again."));
                  setIsSsoLoading(false);
                }
              }}
              disabled={isSsoLoading}
            >
              <svg className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
            </button>
          )}
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:opacity-90 transition-opacity disabled:opacity-60"
            aria-label="Sign in with Instagram"
            onClick={() => {
              setIsSsoLoading(true);
              InstagramAuthCore.startOAuth('login').catch((err) => {
                console.error('Instagram login OAuth error:', err);
                toast.error('Unable to start Instagram login. Please try again.');
                setIsSsoLoading(false);
              });
            }}
            disabled={isSsoLoading}
          >
            <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
          </button>
        </div>
      </div>
      {isSsoLoading && <Loader size="fullscreen" />}
    </AuthLayout>
  );
};
